<template>
	<div
		v-if="item"
		class="g-header-audio-preview"
		:class="[
			`is-layout-${layout}`,
			{ 'is-h5': h5, 'is-playing': isPlaying },
		]"
	>
		<button
			type="button"
			class="g-header-audio-preview__play"
			:aria-label="isPlaying ? '暂停' : '播放'"
			@click="togglePlay"
		>
			<span v-if="isPlaying" class="g-header-audio-preview__pause"></span>
			<span v-else class="g-header-audio-preview__triangle"></span>
		</button>

		<div class="g-header-audio-preview__main">
			<div class="g-header-audio-preview__meta">
				<div class="g-header-audio-preview__name">{{ item.filename }}</div>
				<div class="g-header-audio-preview__size">{{ item.filesize }}</div>
			</div>

			<div class="g-header-audio-preview__progress-wrap">
				<div
					ref="progressTrack"
					class="g-header-audio-preview__progress-track"
					@click="onSeek"
				>
					<div
						class="g-header-audio-preview__progress-bar"
						:style="{ width: `${progressPercent}%` }"
					></div>
					<div
						class="g-header-audio-preview__progress-thumb"
						:style="{ left: `${progressPercent}%` }"
					></div>
				</div>
				<div class="g-header-audio-preview__time">
					<span>{{ currentTimeText }}</span>
					<span>{{ durationText }}</span>
				</div>
			</div>
		</div>

		<audio
			ref="audioEl"
			:src="item.previewUrl"
			preload="metadata"
			@loadedmetadata="onLoadedMetadata"
			@timeupdate="onTimeUpdate"
			@ended="onEnded"
			@pause="onPause"
			@play="onPlay"
		></audio>
	</div>
</template>

<script>
	import baseFunc from '../../config/base_func.js';
	import { formatAudioDuration } from '../utils/localAudio.js';

	export default {
		name: 'GHeaderAudioPreview',
		props: {
			item: {
				type: Object,
				default: null,
			},
			layout: {
				type: String,
				default: 'stack',
			},
			h5: {
				type: Boolean,
				default: false,
			},
			idKey: {
				type: String,
				default: '',
			},
		},
		data() {
			return {
				isPlaying: false,
				currentTime: 0,
				duration: 0,
				hasRequestedPermission: false,
				permissionedAudioUrl: '',
			};
		},
		computed: {
			progressPercent() {
				if (!this.duration) return 0;
				return Math.min(100, (this.currentTime / this.duration) * 100);
			},
			currentTimeText() {
				return formatAudioDuration(this.currentTime);
			},
			durationText() {
				if (this.duration) {
					return formatAudioDuration(this.duration);
				}
				return this.item?.durationText || '00:00';
			},
		},
		watch: {
			item: {
				handler() {
					this.resetPlayer();
				},
				deep: true,
			},
		},
		beforeUnmount() {
			this.pauseAudio();
		},
		methods: {
			resetPlayer() {
				this.pauseAudio();
				this.currentTime = 0;
				this.duration = this.item?.duration || 0;
			},
			onLoadedMetadata() {
				const audio = this.$refs.audioEl;
				if (!audio) return;
				this.duration = Number.isFinite(audio.duration) ? audio.duration : (this.item?.duration || 0);
			},
			onTimeUpdate() {
				const audio = this.$refs.audioEl;
				if (!audio) return;
				this.currentTime = audio.currentTime || 0;
			},
			onPlay() {
				this.isPlaying = true;
			},
			onPause() {
				this.isPlaying = false;
			},
			onEnded() {
				this.isPlaying = false;
				this.currentTime = 0;
				const audio = this.$refs.audioEl;
				if (audio) {
					audio.currentTime = 0;
				}
			},
			togglePlay() {
				const audio = this.$refs.audioEl;
				if (!audio || !this.item?.previewUrl) return;
				if (audio.paused) {
					if (this.h5 && this.item?.id && !this.hasRequestedPermission) {
						this.hasRequestedPermission = true;
						this.requestPlayPermission().then(res => {
							
							audio.play().catch(() => {});
						}).catch(err => {
							this.hasRequestedPermission = false;
							audio.pause();
							this.$message.error(err?.data?.message || '请求失败');
						});
					} else {
						audio.play().catch(() => {});
					}
					return;
				}
				audio.pause();
			},
			//访问路径中取指定的值
			getQueryStringByChannel(name) {
				const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i')
				if (!window.location.href.split('?')[1]) {
					return null
				}
				const re = window.location.href.split('?')[1].match(reg)
				if (re != null) return unescape(re[2])
				return null
			},
			requestPlayPermission() {
				return this.$apiRequest.post('/api/getMediaPlayPermissions', {
					attach_id: this.item.id,
					id_key: this.idKey,
					unique: '',
					p_key: this.getQueryStringByChannel('p_key')==null?'':this.getQueryStringByChannel('p_key'),
				}).then(res => {
					if (res.data?.play_url) {
						this.permissionedAudioUrl = baseFunc.getQiniusign(res.data.play_url);
					}
				});
			},
			pauseAudio() {
				const audio = this.$refs.audioEl;
				if (audio && !audio.paused) {
					audio.pause();
				}
				this.isPlaying = false;
			},
			onSeek(event) {
				const audio = this.$refs.audioEl;
				const track = this.$refs.progressTrack;
				if (!audio || !track || !this.duration) return;
				const rect = track.getBoundingClientRect();
				const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
				audio.currentTime = ratio * this.duration;
				this.currentTime = audio.currentTime;
			},
		},
	};
</script>

<style scoped lang="less">
	.g-header-audio-preview {
		width: 100%;
		box-sizing: border-box;

		&.is-layout-inline {
			display: flex;
			align-items: center;
			gap: 16px;
			padding: 18px 20px;
		}

		&.is-layout-stack {
			display: flex;
			flex-direction: column;
			align-items: center;
			padding: 24px 24px 20px;
		}

		&.is-h5.is-layout-stack {
			padding: 0.48rem 0.3rem 0.4rem;
		}
	}

	.g-header-audio-preview__play {
		flex-shrink: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		border: none;
		background: #285af9;
		cursor: pointer;
		padding: 0;
		outline: none;
	}

	.is-layout-inline .g-header-audio-preview__play {
		width: 44px;
		height: 44px;
		border-radius: 50%;
	}

	.is-layout-stack .g-header-audio-preview__play {
		width: 56px;
		height: 56px;
		border-radius: 50%;
		background: transparent;
		border: 2px solid #285af9;
	}

	.is-h5.is-layout-stack .g-header-audio-preview__play {
		width: 1.12rem;
		height: 1.12rem;
		border-width: 0.04rem;
	}

	.g-header-audio-preview__triangle {
		display: block;
		width: 0;
		height: 0;
		margin-left: 3px;
		border-top: 7px solid transparent;
		border-bottom: 7px solid transparent;
		border-left: 11px solid #fff;
	}

	.is-layout-stack .g-header-audio-preview__triangle {
		border-top-width: 9px;
		border-bottom-width: 9px;
		border-left-width: 14px;
		border-left-color: #285af9;
		margin-left: 4px;
	}

	.is-h5.is-layout-stack .g-header-audio-preview__triangle {
		border-top-width: 0.18rem;
		border-bottom-width: 0.18rem;
		border-left-width: 0.28rem;
		margin-left: 0.08rem;
	}

	.g-header-audio-preview__pause {
		position: relative;
		width: 14px;
		height: 14px;

		&::before,
		&::after {
			content: '';
			position: absolute;
			top: 0;
			width: 4px;
			height: 14px;
			border-radius: 1px;
			background: #fff;
		}

		&::before {
			left: 1px;
		}

		&::after {
			right: 1px;
		}
	}

	.is-layout-stack .g-header-audio-preview__pause {
		&::before,
		&::after {
			background: #285af9;
		}
	}

	.g-header-audio-preview__main {
		min-width: 0;
		flex: 1;
		width: 100%;
	}

	.is-layout-stack .g-header-audio-preview__main {
		margin-top: 16px;
	}

	.is-h5.is-layout-stack .g-header-audio-preview__main {
		margin-top: 0.32rem;
	}

	.g-header-audio-preview__meta {
		min-width: 0;
	}

	.is-layout-inline .g-header-audio-preview__meta {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 12px;
		margin-bottom: 10px;
	}

	.is-layout-stack .g-header-audio-preview__meta {
		text-align: center;
	}

	.g-header-audio-preview__name {
		font-size: 14px;
		color: #333;
		line-height: 1.4;
		word-break: break-all;
	}

	.is-layout-inline .g-header-audio-preview__name {
		flex: 1;
		min-width: 0;
	}

	.is-layout-stack .g-header-audio-preview__name {
		font-size: 15px;
	}

	.is-h5.is-layout-stack .g-header-audio-preview__name {
		font-size: 0.3rem;
	}

	.g-header-audio-preview__size {
		flex-shrink: 0;
		font-size: 12px;
		color: #999;
		line-height: 1.4;
	}

	.is-layout-stack .g-header-audio-preview__size {
		margin-top: 8px;
	}

	.is-h5.is-layout-stack .g-header-audio-preview__size {
		margin-top: 0.16rem;
		font-size: 0.24rem;
	}

	.g-header-audio-preview__progress-wrap {
		width: 100%;
	}

	.is-layout-stack .g-header-audio-preview__progress-wrap {
		margin-top: 18px;
	}

	.is-h5.is-layout-stack .g-header-audio-preview__progress-wrap {
		margin-top: 0.36rem;
	}

	.g-header-audio-preview__progress-track {
		position: relative;
		height: 4px;
		border-radius: 999px;
		background: #e8e8e8;
		cursor: pointer;
	}

	.is-h5 .g-header-audio-preview__progress-track {
		height: 0.06rem;
	}

	.g-header-audio-preview__progress-bar {
		position: absolute;
		left: 0;
		top: 0;
		bottom: 0;
		border-radius: inherit;
		background: #285af9;
	}

	.g-header-audio-preview__progress-thumb {
		position: absolute;
		top: 50%;
		width: 10px;
		height: 10px;
		margin-top: -5px;
		margin-left: -5px;
		border-radius: 50%;
		background: #fff;
		border: 1px solid #d9d9d9;
		box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
	}

	.is-h5 .g-header-audio-preview__progress-thumb {
		width: 0.2rem;
		height: 0.2rem;
		margin-top: -0.1rem;
		margin-left: -0.1rem;
	}

	.g-header-audio-preview__time {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-top: 8px;
		font-size: 12px;
		color: #999;
		line-height: 1.4;
	}

	.is-h5 .g-header-audio-preview__time {
		margin-top: 0.16rem;
		font-size: 0.24rem;
	}
</style>
